import { Button, Card, CardContent, Grid } from '@mui/material'
import { getYear } from 'date-fns'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useBoolean from 'utils/customHook/useBoolean'
import useSandreList from 'utils/customHook/useSandreList'
import { getFileExtension } from 'utils/FileUtils'
import i18n from 'simple-react-i18n'
import { isNil, orderBy, uniqBy } from 'lodash'
import Select from 'components/forms/Select'
import SimpleTabList from 'components/list/SimpleTabList'
import useActions from 'utils/customHook/useActions'
import { getStationArrowNav } from 'utils/ActionUtils'
import { push } from '@lagunovsky/redux-react-router'
import { getLinks } from 'utils/StationUtils'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import DtoQualitometer from 'quality/dto/DtoQualitometer'
import DtoPiezometer from 'piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import DtoQualitometerLight from 'quality/dto/DtoQualitometerLight'
import DtoPiezometerLight from 'piezometry/dto/DtoPiezometerLight'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import ResourceDto from 'resource/dto/ResourceDto'
import DtoFile from 'components/file/dto/DtoFile'
import StationsFileAccordion from './StationsFileAccordion'
import ImportDialog from './ImportDialog'
import Input from 'components/forms/Input'
import { hasValue } from 'utils/NumberUtil'
import MessageCard from 'components/card/MessageCard'

const DATE = 'date'
const FILE = 'file'
const ACTIVE = 'active'

const FilterFields = ({
    defaultFilter = {},
    onValidate = () => {},
    files = [],
}) => {
    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)

    const [searchValue, setSearchValue] = useState(defaultFilter.searchValue)
    const [extension, setExtension] = useState(defaultFilter.extension)
    const [naturePicture, setNaturePicture] = useState(defaultFilter.naturePicture)
    const [natureDocument, setNatureDocument] = useState(defaultFilter.natureDocument)
    const [year, setYear] = useState(defaultFilter.year)
    const [active, setActive] = useState(defaultFilter.active)

    const activeOptions = useMemo(() => [
        { name: i18n.active, code: '1' },
        { name: i18n.inactive, code: '0' },
    ], [])

    const orderedTypePictures = useMemo(() => orderBy(typePictures, 'code'), [typePictures])
    const orderedTypeDocuments = useMemo(() => orderBy(typeDocuments, 'code'), [typeDocuments])

    const onSearch = () => {
        onValidate({
            searchValue,
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        })
    }

    const onReset = () => {
        setSearchValue('')
        setExtension()
        setNaturePicture()
        setNatureDocument()
        setYear()
        setActive()

        onValidate({ searchValue: '' })
    }

    const extensions = useMemo(() => uniqBy(files.map(f => {
        const ext = getFileExtension(f.name)
        return { id: ext, label: ext }
    }), 'id'), [files])

    const years = useMemo(() => {
        const formattedYears = files.map(f => {
            if (isNil(f.date) || isNaN(f.date)) return { id: -1, label: i18n.unknownDate }
            const y = getYear(f.date)
            return { id: y, label: `${y}` }
        })
        return orderBy(uniqBy(formattedYears, 'id'), 'id')
    }, [files])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={2}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                            onEnterKeyPress={onSearch}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.extensions}
                            options={extensions}
                            value={extension}
                            onChange={setExtension}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.pictureType}
                            options={orderedTypePictures}
                            value={naturePicture}
                            onChange={setNaturePicture}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.documentType}
                            options={orderedTypeDocuments}
                            value={natureDocument}
                            onChange={setNatureDocument}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.year}
                            options={years}
                            value={year}
                            onChange={setYear}
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.active}
                            options={activeOptions}
                            value={active}
                            onChange={setActive}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={1} offset={10}>
                        <Button
                            onClick={onReset}
                            variant='outlined'
                            color='primary'
                            fullWidth
                        >
                            {i18n.reinit}
                        </Button>
                    </Grid>
                    <Grid size={1}>
                        <Button
                            onClick={onSearch}
                            variant='contained'
                            color='primary'
                            fullWidth
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterFields.propTypes = {
    defaultFilter: PropTypes.shape({}),
    onValidate: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
}

const PicturesApp = ({
    stations = [],
    station,
    stationType = '',
}) => {
    const [files, setFiles] = useState([])
    const [refetchFiles, setRefetchFiles] = useState(false)

    const dispatch = useDispatch()

    const {
        // for links
        hydrogeologicalEntities,
        distributionUnitLinks,
        installation,
        stationWatermass,

        // linkedStations
        associatedSites,
    } = useSelector(store => ({
        // for links
        hydrogeologicalEntities: store.HydrogeologicalEntityReducer.hydrogeologicalEntities,
        distributionUnitLinks: store.DistributionUnitReducer.distributionUnitLinks,
        installation: store.InstallationReducer.installation,
        stationWatermass: store.StationReducer.stationWatermass,

        // linkedStations
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const [filter, setFilter] = useState({ searchValue: '' })

    const hasCode = hasValue(station?.code)

    const {
        value: isEditMode,
        setTrue: onEdit,
        setFalse: onRead,
    } = useBoolean(false)
    const {
        value: isNewFileOpen,
        setTrue: onNewFileOpen,
        setFalse: onNewFileClose,
    } = useBoolean(false)

    useActions(() => {
        if (!hasCode) return {}
        const objLink = {
            stationTypes: [stationType],
            hydrogeologicalEntities,
            distributionUnitLinks,
            installation,
            stationWatermass,
        }
        const baseActions = {
            links: getLinks(station, objLink),
            arrowNav: getStationArrowNav(stationType, stations, station?.id, s => dispatch(push(`/station/${stationType}/${s.id}/picture`))),
        }
        if (isEditMode) {
            return {
                cancel: onRead,
                new: onNewFileOpen,
                ...baseActions,
            }
        }
        return {
            edit: onEdit,
            ...baseActions,
        }
    }, [isEditMode, stations, station, stationType, hydrogeologicalEntities, distributionUnitLinks, installation, stationWatermass])

    return (
        <Grid container style={{ padding: '5 10 100 10' }} spacing={1} direction='column'>
            {hasCode ? (
                <>
                    <FilterFields
                        defaultFilter={filter}
                        onValidate={setFilter}
                        files={files}
                    />
                    <SimpleTabList
                        defaultTab={DATE}
                        tabs={[{
                            constant: DATE,
                            label: i18n.date,
                            icon: 'insert_invitation',
                        }, {
                            constant: FILE,
                            label: i18n.files,
                            icon: 'insert_drive_file',
                        }, {
                            constant: ACTIVE,
                            label: i18n.active,
                            icon: 'remove_red_eye',
                        }]}
                    >
                        {tab => (
                            <Grid container spacing={1} padding='10px'>
                                {[station, ...associatedSites].map((site, i) => (
                                    <StationsFileAccordion
                                        key={site.stationLinkedCode ?? site.code}
                                        station={site}
                                        stationType={stationType}
                                        group={tab}
                                        filter={filter}
                                        defaultExpanded={i === 0}
                                        isEditMode={i === 0 && isEditMode}
                                        isNewFileOpen={isNewFileOpen}
                                        onNewFileClose={onNewFileClose}
                                        setAllFiles={setFiles}
                                        refetchFiles={i === 0 && refetchFiles}
                                        setRefetchFiles={setRefetchFiles}
                                    />
                                ))}
                            </Grid>
                        )}
                    </SimpleTabList>
                    <ImportDialog
                        isOpen={isNewFileOpen}
                        onClose={onNewFileClose}
                        onValidate={() => setRefetchFiles(true)}
                        files={files}
                        station={station}
                        stationType={stationType}
                        linkSamplePoints={station.link_samplePoints || station.link_pointPrels}
                    />
                </>
            ) : (<MessageCard>{i18n.noFileOrPictureWithoutCode}</MessageCard>)}
        </Grid>
    )
}

PicturesApp.propTypes = {
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoDistributionUnit)),
        PropTypes.arrayOf(ProductionUnitShape),
        PropTypes.arrayOf(PropTypes.instanceOf(ResourceDto)),
    ]),
    station: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(PluviometerDto),
        PropTypes.instanceOf(DtoInstallation),
        PropTypes.instanceOf(DtoDistributionUnit),
        ProductionUnitShape,
        PropTypes.instanceOf(ResourceDto),
    ]),
    stationType: PropTypes.string,
}

export default PicturesApp